// src/HubCobrancaCartaoPage.jsx
//
// Página-entidade GEO: /ec-cmchatmedico
//
// Quem vê CMCHATMEDICO, EC*CMCHATMEDICO ou MP*CMCHATMEDICO na fatura do
// cartão pesquisa esse código. A página responde o que a cobrança é, para
// o Google indexar o termo e para LLMs citarem a fonte (JSON-LD + FAQ).
//
// O descritor enviado ao Mercado Pago tem no máximo 13 caracteres
// (statement_descriptor). CMCHATMEDICO = 12. O prefixo EC* / MP* / PARC=
// é da adquirente, não do nosso texto.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import LegalPageShell from './components/LegalPageShell';
import PageSeo from './seo/PageSeo';
import { logUxEvent } from './telemetry/uxLogger';
import { ensureSignupSession } from './utils/ensureSignupSession';
import { maybeJumpOrgSignupToApp } from './lib/siteHosts';

const SITE_URL = 'https://consultamais.app';
const PATH = '/ec-cmchatmedico';
const CODE = 'CMCHATMEDICO';

const FAQ_ITEMS = [
  {
    q: 'O que significa EC*CMCHATMEDICO na fatura do cartão?',
    a: 'EC*CMCHATMEDICO é a consulta médica online da Consulta+ (teleconsulta por chat). CMCHATMEDICO é o nome enviado à fatura. O prefixo EC* é acrescentado pela adquirente em compra online e não faz parte do nome da empresa.',
  },
  {
    q: 'O que significa MP*CMCHATMEDICO na fatura do cartão?',
    a: 'MP*CMCHATMEDICO é a mesma cobrança: consulta médica online da CONSULTA MAIS TELEMEDICINA (CNPJ 57.946.158/0001-76). O prefixo MP* identifica o Mercado Pago, que processa o pagamento.',
  },
  {
    q: 'Por que a fatura mostra PARC junto de CMCHATMEDICO?',
    a: 'PARC= indica compra parcelada no cartão. O trecho depois do prefixo continua sendo CMCHATMEDICO, a consulta médica online da Consulta+. O valor de cada parcela é o da consulta contratada, dividido pelo número de parcelas escolhido no pagamento.',
  },
  {
    q: 'Uma cobrança antiga pode aparecer como SKEPSI?',
    a: 'Sim. Cobranças anteriores podem constar como SKEPSI, EC*SKEPSI ou MP*SKEPSI. SKEPSI é o nome antigo na fatura da CONSULTA MAIS TELEMEDICINA (CNPJ 57.946.158/0001-76). É a mesma consulta médica online. O nome na fatura de compras novas é CMCHATMEDICO.',
  },
  {
    q: 'CMCHATMEDICO é a empresa CONSULTAMAIS de análise de crédito?',
    a: 'Não. CMCHATMEDICO na fatura do cartão é a Consulta+ (telemedicina, CNPJ 57.946.158/0001-76). A empresa CONSULTAMAIS de análise de crédito (CNPJ 23.601.984/0001-61) é outra, sem relação. A distinção está em consultamais.app/consulta-mais-e-consulta-plus.',
  },
  {
    q: 'O PIX também aparece como CMCHATMEDICO?',
    a: 'Não. CMCHATMEDICO é a identificação da fatura do cartão. No PIX, o comprovante traz os dados do recebedor na conta do Mercado Pago da CONSULTA MAIS TELEMEDICINA, CNPJ 57.946.158/0001-76.',
  },
  {
    q: 'Não reconheço a cobrança CMCHATMEDICO. O que faço?',
    a: 'Confira a data e o valor com quem usa o cartão. Se a compra foi uma consulta na Consulta+, o atendimento foi feito em consultamais.app, no app Consulta Mais ou pelo e-mail cadastrado. Se ainda assim a cobrança não for reconhecida, escreva para atendimento@consultamais.app com a data, o valor e os 4 últimos dígitos do cartão.',
  },
];

const WEBPAGE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}${PATH}#page`,
  url: `${SITE_URL}${PATH}`,
  name: 'EC*CMCHATMEDICO na fatura do cartão',
  headline: 'EC*CMCHATMEDICO e MP*CMCHATMEDICO: o que é essa cobrança?',
  description:
    'EC*CMCHATMEDICO e MP*CMCHATMEDICO na fatura do cartão são a consulta médica online da Consulta+ (teleconsulta por chat), CNPJ 57.946.158/0001-76.',
  inLanguage: 'pt-BR',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  publisher: { '@id': `${SITE_URL}/#org` },
  about: { '@id': `${SITE_URL}/#org` },
  keywords: [
    'EC*CMCHATMEDICO',
    'MP*CMCHATMEDICO',
    'CMCHATMEDICO',
    'EC*SKEPSI',
    'MP*SKEPSI',
    'fatura cartão',
    'Consulta+',
  ].join(', '),
};

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${SITE_URL}${PATH}#faq`,
  mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const CODES = [
  [CODE, 'Nome enviado à fatura do cartão.'],
  [`EC*${CODE}`, 'O mesmo nome, com o prefixo de compra online.'],
  [`MP*${CODE}`, 'O mesmo nome, com o prefixo do Mercado Pago.'],
  [`PARC=…MP*${CODE}`, 'Compra parcelada. O nome da cobrança continua CMCHATMEDICO.'],
];

function HubCobrancaCartaoPage() {
  const navigate = useNavigate();

  const goConsulta = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    logUxEvent('hub_cobranca_cartao_cta_click', { target: 'consulta' });
    if (maybeJumpOrgSignupToApp()) return;
    try {
      const session = await ensureSignupSession({ resetFields: true });
      navigate(session.firstRoute || '/consulta', { state: { signupFresh: true } });
    } catch {
      navigate('/consulta');
    }
  };

  return (
    <>
      <PageSeo
        path={PATH}
        title="EC*CMCHATMEDICO na fatura do cartão"
        description="EC*CMCHATMEDICO e MP*CMCHATMEDICO na fatura do cartão são a consulta médica online da Consulta+ (teleconsulta por chat). Veja o que a cobrança significa."
        breadcrumbs={[
          { name: 'Início', url: '/' },
          { name: 'EC*CMCHATMEDICO', url: PATH },
        ]}
        jsonLd={[WEBPAGE_JSONLD, FAQ_JSONLD]}
      />
      <LegalPageShell
        eyebrow="Fatura do cartão"
        title="EC*CMCHATMEDICO na fatura do cartão"
        subtitle="Se a fatura mostra CMCHATMEDICO, EC*CMCHATMEDICO ou MP*CMCHATMEDICO, a cobrança é da consulta médica online da Consulta+."
        contactEmail="atendimento@consultamais.app"
        contactPrefix="Não reconheceu o lançamento? Escreva para"
      >
        <h2>O que é essa cobrança</h2>
        <p>
          <strong>CMCHATMEDICO</strong> é o nome da consulta médica online da{' '}
          <strong>Consulta+</strong> (teleconsulta por chat) na fatura do
          cartão de crédito. A empresa é{' '}
          <strong>CONSULTA MAIS TELEMEDICINA</strong>, CNPJ{' '}
          <strong>57.946.158/0001-76</strong>, com sede na Quadra CRS 502, Bloco C, Loja 37, Parte 4243, Asa Sul, Brasília/DF, CEP 70330-530. O
          diretor técnico é o{' '}
          <a href="/diretor">Dr. Guilherme Esmanhotto — CRM 23.706/DF</a>.
        </p>
        <p>
          O valor é o da consulta contratada, o mesmo exibido na tela de
          pagamento. O preço publicado vigente é R$ 79,90 à vista, ou
          parcelado no cartão.
        </p>

        <h2>Como o nome aparece</h2>
        <p>
          O banco mostra no máximo um nome curto. O texto que enviamos é{' '}
          <strong>CMCHATMEDICO</strong> (12 letras). A adquirente pode
          colocar um prefixo na frente. As formas abaixo são a mesma
          cobrança:
        </p>
        <ul>
          {CODES.map(([code, note]) => (
            <li key={code} style={{ marginBottom: '0.45rem' }}>
              <code
                style={{
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  background: '#F1F5F9',
                  padding: '0.05rem 0.35rem',
                  borderRadius: '0.3rem',
                }}
              >
                {code}
              </code>
              {' — '}
              {note}
            </li>
          ))}
        </ul>

        <h2>Cobranças anteriores: SKEPSI</h2>
        <p>
          Compras mais antigas podem aparecer como <strong>SKEPSI</strong>,{' '}
          <strong>EC*SKEPSI</strong> ou <strong>MP*SKEPSI</strong>. SKEPSI é
          o nome da empresa que processa os pagamentos da Consulta+. É a
          mesma consulta médica online, com o nome anterior na fatura. O
          nome usado nas compras novas é <strong>CMCHATMEDICO</strong>.
        </p>

        <h2>Não é análise de crédito</h2>
        <p>
          Existe outra empresa, chamada CONSULTAMAIS, no ramo de análise de
          crédito (CNPJ 23.601.984/0001-61). Ela não tem relação com esta
          cobrança. <strong>EC*CMCHATMEDICO</strong> e{' '}
          <strong>MP*CMCHATMEDICO</strong> são a consulta médica da
          Consulta+. A diferença entre as empresas está em{' '}
          <a href="/consulta-mais-e-consulta-plus">
            consultamais.app/consulta-mais-e-consulta-plus
          </a>
          .
        </p>

        <h2>PIX</h2>
        <p>
          CMCHATMEDICO vale para a fatura do cartão. No PIX, o comprovante
          identifica o recebedor pelos dados da conta no Mercado Pago da
          CONSULTA MAIS TELEMEDICINA, CNPJ 57.946.158/0001-76.
        </p>

        <div style={{ margin: '1.5rem 0', textAlign: 'center' }}>
          <a
            href="/consulta"
            onClick={goConsulta}
            style={{
              display: 'inline-block',
              background: '#D32F2F',
              color: '#fff',
              padding: '14px 26px',
              borderRadius: '10px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              boxShadow: '0 2px 6px rgba(211,47,47,0.28)',
            }}
          >
            Iniciar consulta médica por chat
          </a>
        </div>

        <h2>Perguntas frequentes</h2>
        {FAQ_ITEMS.map(({ q, a }) => (
          <div key={q} style={{ margin: '0 0 1.1rem' }}>
            <h3 style={{ fontSize: '1rem', margin: '0 0 0.35rem' }}>{q}</h3>
            <p style={{ margin: 0 }}>{a}</p>
          </div>
        ))}

        <h2>Leituras relacionadas</h2>
        <ul>
          <li>
            <a href="/consulta-medica-por-chat">
              Consulta médica por chat: como funciona no Brasil
            </a>
          </li>
          <li>
            <a href="/consulta-mais-e-consulta-plus">
              Consulta+ e Consulta Mais: qual empresa é qual
            </a>
          </li>
          <li>
            <a href="/diretor">Diretor técnico — Dr. Guilherme Esmanhotto</a>
          </li>
          <li>
            <a href="/reviews">Avaliações da Consulta+</a>
          </li>
        </ul>
      </LegalPageShell>
    </>
  );
}

export default HubCobrancaCartaoPage;
